import { patchApiConfigInWorkflow, WorkflowPayload } from '../api/patchApiConfigInWorkflow';
import { generateFSNodeSchema } from '@/utils/generateFSNodeSchema';
import { updateWorkflowWithCheckpoint } from '@/features/projects/hooks/updateWorkflowWithCheckpoint';
import { getProjectWorkflowById } from '@/features/projects/api/getProjectWorkflowById';

export interface UpdateApiConfigOfWorkflowProps {
  projectId: string;
  workflow_id: string;
  savedApiWorkflow: any;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  addSingle: (args: { project_id: string; payload: any }) => void;
  commitMessage: string;
  commitAuthor: string;
  normal_workflow_file_id: string;
  api_workflow_file_id: string;
  newVersion: number;
  workflowName?: string;
}

export const updateApiConfigOfWorkflow = async ({
  projectId,
  workflow_id,
  savedApiWorkflow,
  onSuccess,
  onError,
  addSingle,
  commitMessage,
  newVersion,
  commitAuthor,
  normal_workflow_file_id,
  api_workflow_file_id,
  workflowName,
}: UpdateApiConfigOfWorkflowProps): Promise<void> => {
  try {
    // 1. Fetch all workflows for this project
    const wf = await getProjectWorkflowById({ project_id: projectId, workflow_id });

    // Generate FS node schema from saved API workflow
    const { fsInputs: newFSInputs, fsOutputs: newFSOutputs } =
      generateFSNodeSchema(savedApiWorkflow);

    if (!wf || !wf.api_file_id) {
      throw new Error(`Workflow ${workflow_id} not found in project ${projectId}`);
    }

    // 3. Preserve existing is_manual (or default false)
    const existingApiConfig = wf.api_config ?? { inputs: [], outputs: [], is_manual: false };

    // Use a regex to remove existing FS node entries from inputs
    const fsInputPattern = /^\d+\.inputs\./;
    const nonFSInputs = (wf.inputs || []).filter((inp: any) => !fsInputPattern.test(inp.path));

    // Build a lookup from existing FS inputs (by nodeId) to preserve parameter_name
    const existingFSEntries: { [nodeId: string]: any } = {};
    (wf.inputs || []).forEach((inp: any) => {
      const parts = inp.path.split('.');
      if (parts && parts.length > 0 && fsInputPattern.test(inp.path)) {
        existingFSEntries[parts[0]] = inp;
      }
    });

    // For each new FS input, if an entry with the same nodeId exists, preserve its parameter_name
    const mergedFSInputs = newFSInputs.map((newEntry) => {
      const nodeId = newEntry.path.split('.')[0];
      if (existingFSEntries[nodeId] && existingFSEntries[nodeId].parameter_name) {
        return { ...newEntry, parameter_name: existingFSEntries[nodeId].parameter_name };
      }
      return newEntry;
    });

    const hasFSNodes = mergedFSInputs.length > 0 || newFSOutputs.length > 0;
    let finalInputs = [];
    let finalOutputs = [];

    if (typeof wf.is_manual === 'undefined' || wf.is_manual === null) {
      // If advancedConfig is not present, set it to false
      wf.is_manual = false;
      finalInputs = [...nonFSInputs, ...mergedFSInputs];
      finalOutputs = newFSOutputs;
    } else if (!wf.is_manual && hasFSNodes) {
      // If advancedConfig is false and FS nodes are present, update inputs/outputs
      wf.inputs = [...nonFSInputs, ...mergedFSInputs];
      wf.outputs = newFSOutputs;
      wf.is_manual = false;
      finalInputs = wf.inputs;
      finalOutputs = wf.outputs;
    } else {
      // In all other cases, use the merged inputs and outputs
      finalInputs = [...nonFSInputs, ...mergedFSInputs];
      finalOutputs = newFSOutputs;
    }

    // 4. Build new payload
    const payload: WorkflowPayload = {
      description: wf.description,
      api_config: {
        inputs: finalInputs,
        outputs: finalOutputs,
        is_manual: existingApiConfig.is_manual,
      },
    };

    // 5. Patch it
    await patchApiConfigInWorkflow({
      project_id: projectId,
      workflow_id,
      payload,
    });

    // 6. Create checkpoint if addSingle is provided
    // if (addSingle && normal_workflow_file_id && api_workflow_file_id) {
    await updateWorkflowWithCheckpoint({
      projectId,
      workflow_id,
      workflowName: workflowName || wf?.name,
      normal_workflow_file_id,
      api_workflow_file_id,
      newVersion: newVersion === 0 ? 0 : newVersion,
      checkpointInputs: finalInputs,
      checkpointOutputs: finalOutputs,
      is_manual: existingApiConfig.is_manual,
      commitMessage,
      commitAuthor,
      addSingle,
    });
    // }

    // 7. Success callback
    onSuccess?.();
  } catch (error) {
    console.error('Failed to update API config of workflow:', error);
    onError?.(error);
  }
};
